import { MuiStyles } from "@leap4/frontend";
import { brandColors, numericFontFamily } from "./colors";

const { createTheme } = MuiStyles;

type Theme = MuiStyles.Theme;
export type ThemeMode = "light" | "dark";

// Chrome paints autofill on the <input>, so an adorned field turns two-toned unless the input is repainted.
const autofillStyles = (surface: string, text: string) => ({
    "&:-webkit-autofill, &:-webkit-autofill:hover, &:-webkit-autofill:focus, &:-webkit-autofill:active": {
        WebkitBoxShadow: `0 0 0 100px ${surface} inset`,
        WebkitTextFillColor: text,
        caretColor: text,
        borderRadius: "inherit",
        transition: "background-color 600000s 0s, color 600000s 0s",
    },
});

const tokensFor = (mode: ThemeMode) => {
    const dark = mode === "dark";
    return {
        dark,
        background: dark ? brandColors.background.dark : brandColors.background.light,
        surface: dark ? brandColors.surface.dark : brandColors.surface.light,
        surface2: dark ? brandColors.surface2.dark : brandColors.surface2.light,
        surface3: dark ? brandColors.surface3.dark : brandColors.surface3.light,
        text: dark ? brandColors.txt : brandColors.lightTxt,
        text2: dark ? brandColors.txt2 : brandColors.lightTxt2,
        text3: dark ? brandColors.txt3 : brandColors.lightTxt3,
        border: dark ? brandColors.border : brandColors.lightBorder,
        border2: dark ? brandColors.border2 : brandColors.lightBorder2,
    };
};

export const createPortalTheme = (mode: ThemeMode): Theme => {
    const t = tokensFor(mode);

    // Only the dark DSP theme carries these; the light one relies on MUI defaults for them.
    const darkOnly = t.dark
        ? {
              MuiCssBaseline: {
                  styleOverrides: {
                      body: {
                          scrollbarColor: `${t.surface3} transparent`,
                          "&::-webkit-scrollbar": { width: 8, height: 8 },
                          "&::-webkit-scrollbar-thumb": { backgroundColor: t.surface3, borderRadius: 4 },
                          "&::-webkit-scrollbar-track": { backgroundColor: "transparent" },
                      },
                  },
              },
              MuiAppBar: { styleOverrides: { colorDefault: { backgroundColor: t.surface, backgroundImage: "none" } } },
              MuiDrawer: { styleOverrides: { paper: { backgroundColor: t.surface, borderColor: t.border } } },
              MuiSelect: { styleOverrides: { root: { fontSize: "0.8125rem" } } },
              MuiIconButton: { styleOverrides: { root: { "&:hover": { backgroundColor: t.surface2 } } } },
              MuiTooltip: {
                  styleOverrides: {
                      tooltip: { backgroundColor: t.surface3, border: `1px solid ${t.border2}`, fontSize: "0.75rem" },
                  },
              },
              MuiDivider: { styleOverrides: { root: { borderColor: t.border } } },
              MuiLinearProgress: {
                  styleOverrides: { root: { borderRadius: 4, backgroundColor: t.surface3 }, bar: { borderRadius: 4 } },
              },
          }
        : {};

    const overlayPaper = {
        border: `1px solid ${t.border2}`,
        borderRadius: 12,
        ...(t.dark && { backgroundColor: t.surface, backgroundImage: "none", boxShadow: "0 8px 32px rgba(0,0,0,0.5)" }),
    };

    return createTheme({
        palette: {
            mode,
            primary: { main: brandColors.primary, light: brandColors.primaryLight, dark: brandColors.primaryDark },
            secondary: t.dark
                ? { main: brandColors.secondaryLight }
                : { main: brandColors.secondary, light: brandColors.secondaryLight, dark: brandColors.secondaryDark },
            success: { main: t.dark ? brandColors.green : "#16a34a" },
            warning: { main: t.dark ? brandColors.amber : "#d97706" },
            error: { main: t.dark ? brandColors.red : "#dc2626" },
            info: { main: t.dark ? brandColors.info : "#2563eb" },
            background: { default: t.background, paper: t.surface },
            text: { primary: t.text, secondary: t.text2, disabled: t.text3 },
            divider: t.border,
            action: { hover: t.surface2, selected: t.surface3, focus: t.surface2 },
        },
        typography: {
            fontFamily: '"DM Sans", -apple-system, BlinkMacSystemFont, sans-serif',
            h1: { fontSize: "2rem", fontWeight: 600, letterSpacing: "-0.02em" },
            h2: { fontSize: "1.5rem", fontWeight: 600, letterSpacing: "-0.02em" },
            h3: { fontSize: "1.25rem", fontWeight: 600 },
            h4: { fontSize: "1.5rem", fontWeight: 600, letterSpacing: "-0.02em" },
            h5: { fontSize: "1.125rem", fontWeight: 600 },
            h6: { fontSize: "1rem", fontWeight: 600 },
            body1: { fontSize: "0.8125rem" },
            body2: { fontSize: "0.8125rem" },
            caption: { fontSize: "0.75rem" },
            button: { textTransform: "none" as const, fontWeight: 500 },
        },
        shape: { borderRadius: 8 },
        components: {
            ...darkOnly,
            MuiPaper: { styleOverrides: { root: { backgroundImage: "none" } } },
            MuiPopover: { styleOverrides: { paper: overlayPaper } },
            MuiMenu: { styleOverrides: { paper: overlayPaper } },
            MuiDialog: {
                styleOverrides: {
                    paper: t.dark
                        ? { backgroundColor: t.surface, border: `1px solid ${t.border2}`, borderRadius: 12, backgroundImage: "none" }
                        : { borderRadius: 12 },
                },
            },
            MuiCard: {
                styleOverrides: {
                    root: {
                        border: `1px solid ${t.border}`,
                        ...(t.dark && { backgroundColor: t.surface, backgroundImage: "none" }),
                    },
                },
            },
            MuiDataGrid: {
                styleOverrides: {
                    root: {
                        borderColor: t.border,
                        fontSize: "0.8125rem",
                        "--DataGrid-containerBackground": t.surface2,
                        "& .MuiDataGrid-cell--textRight, & .MuiDataGrid-columnHeader--alignRight": {
                            fontFamily: numericFontFamily,
                            fontVariantNumeric: "tabular-nums",
                        },
                    },
                    cell: { borderColor: t.border },
                    columnHeaders: { backgroundColor: t.surface2, borderColor: t.border },
                    columnHeader: { "&:focus, &:focus-within": { outline: "none" } },
                    footerContainer: { backgroundColor: t.surface2, borderColor: t.border },
                },
            },
            MuiTextField: {
                styleOverrides: {
                    root: t.dark
                        ? {
                              "& .MuiOutlinedInput-root": {
                                  "& fieldset": { borderColor: t.border2 },
                                  "&:hover fieldset": { borderColor: t.text3 },
                                  "&.Mui-focused fieldset": { borderColor: brandColors.accent },
                              },
                              "& .MuiInputBase-input": { fontSize: "0.8125rem" },
                          }
                        : { "& .MuiInputBase-input": { fontSize: "0.8125rem" } },
                },
            },
            MuiOutlinedInput: {
                styleOverrides: {
                    ...(t.dark && {
                        root: {
                            "& fieldset": { borderColor: t.border2 },
                            "&:hover fieldset": { borderColor: t.text3 },
                            "&.Mui-focused fieldset": { borderColor: brandColors.accent },
                        },
                    }),
                    input: { fontSize: "0.8125rem", ...autofillStyles(t.surface, t.text) },
                },
            },
            MuiButton: {
                defaultProps: { disableElevation: true },
                styleOverrides: {
                    root: { textTransform: "none", fontWeight: 500, borderRadius: 8, fontSize: "0.8125rem" },
                    contained: {
                        backgroundColor: brandColors.primary,
                        "&:hover": { backgroundColor: brandColors.primaryDark },
                    },
                    ...(t.dark && {
                        outlined: {
                            borderColor: t.border,
                            color: t.text2,
                            "&:hover": { borderColor: t.border2, backgroundColor: t.surface2, color: t.text },
                        },
                        text: { "&:hover": { backgroundColor: t.surface2 } },
                    }),
                },
            },
            MuiCheckbox: {
                styleOverrides: {
                    root: t.dark
                        ? {
                              color: t.border2,
                              borderRadius: 4,
                              "&.Mui-checked": { color: brandColors.accent },
                              "& .MuiSvgIcon-root": { fontSize: "1.2rem" },
                          }
                        : { "&.Mui-checked": { color: brandColors.primary } },
                },
            },
            MuiTableCell: {
                styleOverrides: {
                    root: { borderColor: t.border, fontSize: "0.8125rem" },
                    head: {
                        backgroundColor: t.surface2,
                        fontSize: "0.6875rem",
                        fontWeight: 500,
                        textTransform: "uppercase",
                        letterSpacing: "0.04em",
                        // DSP uses text.disabled here; secondary keeps the header label above 4.5:1.
                        color: t.text2,
                    },
                },
            },
            MuiTableRow: { styleOverrides: { root: { "&:hover": { backgroundColor: `${t.surface2} !important` } } } },
            MuiTabs: { styleOverrides: { indicator: { backgroundColor: brandColors.primary } } },
            MuiTab: { styleOverrides: { root: { textTransform: "none", fontWeight: 500, fontSize: "0.8125rem" } } },
            MuiChip: {
                styleOverrides: {
                    root: { fontSize: "0.75rem", fontWeight: 500, borderRadius: 6 },
                    ...(t.dark && { outlined: { borderColor: t.border } }),
                },
            },
            MuiListItemButton: {
                styleOverrides: {
                    root: {
                        borderRadius: 6,
                        ...(t.dark && { "&:hover": { backgroundColor: t.surface2 } }),
                        "&.Mui-selected": t.dark
                            ? {
                                  backgroundColor: brandColors.accentSoft,
                                  color: brandColors.accent,
                                  "&:hover": { backgroundColor: brandColors.accentSoft },
                              }
                            : {
                                  backgroundColor: "rgba(124,110,240,0.08)",
                                  color: brandColors.primaryDark,
                                  "&:hover": { backgroundColor: "rgba(124,110,240,0.12)" },
                              },
                    },
                },
            },
            MuiAlert: { styleOverrides: { root: { borderRadius: 8, fontSize: "0.8125rem" } } },
            MuiSkeleton: { styleOverrides: { root: { backgroundColor: t.surface2 } } },
        },
    });
};

export const lightTheme = createPortalTheme("light");
export const darkTheme = createPortalTheme("dark");
